import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TitleButton from '../components/title.jsx';
import { useAuth } from '../context/AuthContext';

const API = (path, token, opts = {}) =>
	fetch(path, { ...opts, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers } });

export default function List() {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const token = localStorage.getItem('token');

	const [titles, setTitles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [titleValue, setTitleValue] = useState('');
	const [selectedTitle, setSelectedTitle] = useState('');
	const [feedback, setFeedback] = useState('');

	const fetchTitles = async () => {
		setLoading(true);
		try {
			const res = await API('/api/questions/titles', token);
			if (res.status === 401) {
				logout();
				navigate('/login', { replace: true });
				return;
			}

			if (res.ok) {
				const data = await res.json();
				setTitles(Array.isArray(data) ? data : []);
			}
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (token) fetchTitles();
	}, [token]);

	const normalizedTitleItems = useMemo(() => {
		const blockedTitles = new Set(['General Business']);
		const resolvedTitles = titles
			.map((item) => {
				if (typeof item === 'string') {
					return { title: item.trim(), count: '' };
				}

				return {
					title: typeof item?.title === 'string' ? item.title.trim() : '',
					count: item?.count ?? ''
				};
			})
			.filter((item) => item.title && !blockedTitles.has(item.title));

		return resolvedTitles
			.filter((item, index, arr) => arr.findIndex((candidate) => candidate.title === item.title) === index)
			.sort((a, b) => a.title.localeCompare(b.title));
	}, [titles]);

	const goToQuestions = (title) => {
		if (!title?.trim()) return;
		const params = new URLSearchParams({ title });
		navigate(`/title?${params.toString()}`);
	};

	const handleCreateTitle = async (event) => {
		event?.preventDefault();

		const nextTitle = titleValue.trim();
		if (!nextTitle) {
			setFeedback('Enter a business title first.');
			return;
		}

		setSaving(true);
		setFeedback('');

		try {
			const res = await API('/api/questions/titles', token, {
				method: 'POST',
				body: JSON.stringify({ title: nextTitle })
			});

			if (res.status === 401) {
				logout();
				navigate('/login', { replace: true });
				return;
			}

			const data = await res.json().catch(() => ({}));
			if (!res.ok) {
				setFeedback(data.message || 'Could not save the business title.');
				return;
			}

			setSelectedTitle(data.title || nextTitle);
			setTitleValue('');
			setFeedback('Business title saved to MongoDB.');
			await fetchTitles();
		} catch (err) {
			console.error(err);
			setFeedback('Network error saving the business title.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="page" style={{ maxWidth: '820px' }}>
			{feedback && (
				<div className={`alert ${feedback === 'Business title saved to MongoDB.' ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '1rem' }}>
					{feedback}
				</div>
			)}

			<TitleButton
				variant="form"
				value={titleValue}
				onChange={(event) => setTitleValue(event.target.value)}
				onSubmit={handleCreateTitle}
				disabled={!titleValue.trim() || saving}
				loading={loading}
				titleItems={normalizedTitleItems}
				activeTitle={selectedTitle}
				onTitleSelect={goToQuestions}
				onClearSelection={() => setSelectedTitle('')}
				onActiveTitleOpen={goToQuestions}
				submitLabel={saving ? 'Saving...' : 'Save Title'}
			/>
		</div>
	);
}

